import { Spacing, Text } from '@vkontakte/vkui';
import img1tb from 'img/quota-landing/skipped-payment-1tb.png';
import img2tb from 'img/quota-landing/skipped-payment-2tb.png';
import img4tb from 'img/quota-landing/skipped-payment-4tb.png';
import img256 from 'img/quota-landing/skipped-payment-256.png';
import img512 from 'img/quota-landing/skipped-payment-512.png';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { skippedPayment, skippedPaymentTrials7d } from 'reactApp/appHelpers/featuresHelpers/features/skippedPayments';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { openSkippedPaymentFinalModal } from 'reactApp/sections/QuotaLanding/SkippedPaymentFinalDialog/SkippedPaymentFinalDialog.helpers';
import type { RootState } from 'reactApp/store';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getPeriodName, getTariffProductsByPeriods, isMonthPeriod } from 'reactApp/utils/Period';

import styles from './SkippedPaymentDialog.css';
import type { SkippedPaymentDialogProps } from './SkippedPaymentDialog.types';

const images = {
    '256': img256,
    '512': img512,
    '1': img1tb,
    '2': img2tb,
    '4': img4tb,
};

const sendRadar = (action: 'click' | 'show' | 'close') => {
    sendPaymentGa({
        eventCategory: ECategoryGa.modal,
        action,
        name: 'mnz_trial7_in_abandoned_payment',
    });
};

export const SkippedPaymentDialog = memo<SkippedPaymentDialogProps>(({ onClose, product }) => {
    const dispatch = useDispatch();

    const space = product.space.space;
    const productType = product?.isFamily ? 'family' : 'regular';
    const trialIdBySpace = skippedPaymentTrials7d[productType]?.[space];
    const trial = useSelector((state: RootState) => ProductsSelectors.getTariffById(state, trialIdBySpace));

    const img = images[space];

    const handleClick = useCallback(() => {
        const { yearProduct: yearTrial, monthProduct: monthTrial } = getTariffProductsByPeriods(trial?.products || []);
        const productId = isMonthPeriod(product.period) ? monthTrial.id : yearTrial.id;
        const trialPeriod = isMonthPeriod(product.period) ? monthTrial.trialPeriod : yearTrial.trialPeriod;

        dispatch(
            openTariffBuy({
                productId,
                checkAnonymous: false,
                isMobile: false,
                paySource: 'trial7_in_abandoned_payment',
                title: `Подписка Mail Space на ${getPeriodName(trialPeriod || '', false, false, true)} бесплатно`,
                hideOtherTariffs: true,
            })
        );
        sendRadar('click');
        emitAnalyticEvent(AnalyticEventNames.SKIPPED_PAYMENT_MODAL_7D_CLICK);
        onClose?.();
    }, [dispatch, product, trial, onClose]);

    const handleClose = useCallback(() => {
        if (skippedPayment === 'c') {
            openSkippedPaymentFinalModal({ product });
        }
        sendRadar('close');
        onClose?.();
    }, [onClose, product]);

    useEffect(() => {
        sendRadar('show');
        emitAnalyticEvent(AnalyticEventNames.SKIPPED_PAYMENT_MODAL_7D_SHOW);
    }, []);

    const content = useMemo(() => {
        return (
            <>
                <Spacing size={12} />
                <Text className={styles.text}>
                    Вы выбрали тариф {product.space.value} и его можно попробовать неделю бесплатно. Это ваше время, за которое сможете
                    взвесить все за и против и принять решение
                </Text>
                <Spacing size={24} />
            </>
        );
    }, [product]);

    if (!trial) {
        return null;
    }

    return (
        <WhatsNewDialog
            imageUrl={img}
            title="Можно попробовать за просто так"
            content={content}
            primaryButtonText="Попробовать за просто так"
            onClick={handleClick}
            onClose={handleClose}
            imageBgColor="var(--vkui--color_background_primary)"
            buttonTheme="vk"
            dialogSize="tiny"
            qaId="skipped-payment-modal"
            isRebranding
        />
    );
});

SkippedPaymentDialog.displayName = 'SkippedPaymentDialog';
